import { createAsyncThunk } from 'legacy/lut/store/utils/createAsyncThunk'

import { TDownloadActions } from './createDownloadActions'

type IDownloadProgressActions = Pick<TDownloadActions, 'downloadStarted' | 'downloadProgress'>

/** A response with an extra listener for the bytes downloaded, besides the store's progress. */
interface IDownloadWithProgressArgs {
  response: Response
  onProgress?: (loadedBytes: number, totalBytes: number | null) => void
}

export const createDownloadWithProgressThunk = (
  typePrefix: string,
  actions: IDownloadProgressActions
) =>
  createAsyncThunk(typePrefix, async (args: Response | IDownloadWithProgressArgs, { dispatch }) => {
    const { response, onProgress }: IDownloadWithProgressArgs =
      args instanceof Response ? { response: args } : args

    if (!response.ok) {
      throw new Error(`Request failed with status: ${response.status}`)
    }

    const contentLengthHeader = response.headers.get('content-length')
    const totalBytes = contentLengthHeader ? Number(contentLengthHeader) : null

    dispatch(actions.downloadStarted({ totalBytes }))

    if (!response.body) {
      return await response.arrayBuffer()
    }

    const reader = response.body.getReader()
    let loadedBytes = 0
    const chunks: Uint8Array[] = []

    while (true) {
      const { done, value } = await reader.read()

      if (done) break

      if (value) {
        chunks.push(value)
        loadedBytes += value.byteLength

        dispatch(actions.downloadProgress({ loadedBytes }))
        onProgress?.(loadedBytes, totalBytes)
      }
    }

    const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0)
    const result = new Uint8Array(totalLength)
    let offset = 0
    for (const chunk of chunks) {
      result.set(chunk, offset)
      offset += chunk.length
    }

    return result.buffer as ArrayBuffer
  })
